import axios from 'axios';
import UrlFactory from './url_factory';
import { getAuthCookie } from '../utils/cookies';
import { companyHeaders } from '../company/session';

class API extends UrlFactory {
    static _instance = null;

    constructor() {
        super();
        if (!API._instance) {
            API._instance = this;
        }
    }

    static Instance() {
        if (!API._instance) {
            API._instance = new API();
        }
        return API._instance;
    }

    /** Forget the singleton (company reset). */
    static resetInstance() {
        API._instance = null;
    }

    async makeHeaders() {
        // Always get the current token from cookies instead of caching it
        const currentToken = getAuthCookie();
        return {
            // x-client identifies the admin web app so the service exposes the real userType
            // (e.g. TradeMarketing) instead of the mobile-app analyst presentation.
            // x-company names the signed-in company (Mary without a session); each API answers 421
            // to another company's id.
            headers: { token: currentToken, 'x-client': 'admin', ...companyHeaders() },
            validateStatus: (status) => [200, 201, 202, 204, 304, 400, 404, 409, 422, 500].includes(status)
        };
    }

    // -------- USER --------
    async requestSignIn(data) {
        // Identify the admin web app so TradeMarketing users sign in as TradeMarketing
        // (the mobile app, which sends no x-client header, receives the analyst presentation).
        const response = await axios.post(this.createSignInUrl(), data, { headers: { 'x-client': 'admin', ...companyHeaders() } });
        return await response.data;
    }

    /**
     * One password-recovery step ('forgot' | 'verify' | 'reset') at `company`'s API, with its
     * x-company. The company is the one central-service names for the typed email
     * (forgot-password page): no session is read or stored and no token is sent.
     * Resolves `{ status, data }` for any answer below 500; network errors and 5xx throw.
     * @param {'forgot'|'verify'|'reset'} step
     * @param {{ companyId: string, apiUrl: string }} company - company/session.js shape
     * @param {Object} payload
     */
    async requestPasswordStep(step, company, payload) {
        const response = await axios.post(this.createPasswordUrl(step, company.apiUrl), payload, {
            headers: { 'x-client': 'admin', 'x-company': company.companyId },
            timeout: 15000,
            validateStatus: (status) => status >= 200 && status < 500
        });
        return { status: response.status, data: response.data };
    }

    async requestCreateUser(payload) {
        const response = await axios.post(this.createUserUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateUserById(id, payload) {
        const response = await axios.post(this.createUpdateUserByIdUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUserByToken() {
        const response = await axios.get(this.createUserByTokenUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUserById(id) {
        const response = await axios.get(this.createUserByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestUsers() {
        const response = await axios.get(this.createAllUsersUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUsersByUserType(userType) {
        const response = await axios.get(this.createUsersByUserTypeUrl(userType), await this.makeHeaders());
        return await response.data;
    }

    async requestPromotorsStats(channel = 'all', forceRefresh = false, clientId = null) {
        const response = await axios.get(this.createPromotorsStatsUrl(channel, forceRefresh, clientId), await this.makeHeaders());
        return await response.data;
    }

    // -------- NOTIFICATIONS --------
    async requestNotifications(filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createNotificationsUrl()}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestNotificationById(id) {
        const response = await axios.get(this.createNotificationByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestNotificationStats() {
        const response = await axios.get(this.createNotificationStatsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestNotificationUnreadCount() {
        const response = await axios.get(this.createNotificationUnreadCountUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestMarkNotificationAsRead(id, userId) {
        const response = await axios.post(this.createMarkNotificationAsReadUrl(id), { userId }, await this.makeHeaders());
        return await response.data;
    }

    async requestMarkMultipleNotificationsAsRead(notificationIds, userId) {
        const response = await axios.post(this.createMarkMultipleNotificationsAsReadUrl(), { notificationIds, userId }, await this.makeHeaders());
        return await response.data;
    }

    async requestDismissNotification(id, userId) {
        const response = await axios.post(this.createDismissNotificationUrl(id), { userId }, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteNotification(id) {
        const response = await axios.delete(this.createDeleteNotificationUrl(id), await this.makeHeaders());
        return await response.data;
    }

    // -------- CLIENTS --------
    async requestCreateClient(payload) {
        const response = await axios.post(this.createClientUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestClients() {
        const headers = await this.makeHeaders();
        const response = await axios.get(this.createGetClientsUrl(), headers);
        return await response.data;
    }

    async requestClientById(id) {
        const response = await axios.get(this.createGetClientByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateClientById(id, payload) {
        const response = await axios.post(this.createUpdateClientByIdUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    // -------- STORES --------
    async requestBulkDeactivateStores(storeIds) {
        const response = await axios.post(this.createBulkDeactivateStoresUrl(), { storeIds }, await this.makeHeaders());
        return response.data;
    }

    async requestCreateStore(payload) {
        const response = await axios.post(this.createStoreUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestStores(page = 1, limit = 1000, search = '', includePromotors = false) {
        const url = this.createGetStoresUrl();
        const params = new URLSearchParams();
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);
        if (search) params.append('search', search);
        if (includePromotors) params.append('includePromotors', 'true');

        const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
        const response = await axios.get(fullUrl, await this.makeHeaders());
        const data = await response.data;

        // ✅ Return the full response with pagination metadata
        if (data.stores && data.pagination) {
            return data; // Return the full response with stores and pagination
        }

        // ✅ Fallback for old response format (backward compatibility)
        return Array.isArray(data) ? data : [];
    }

    async requestAllStores(includePromotors = false) {
        // ✅ Fetch ALL stores by using a very high limit
        const url = this.createGetStoresUrl();
        const params = new URLSearchParams();
        params.append('limit', '50000');
        if (includePromotors) params.append('includePromotors', 'true');

        const response = await axios.get(`${url}?${params.toString()}`, await this.makeHeaders());
        const data = await response.data;

        // ✅ Handle new paginated response format
        if (data.stores && data.pagination) {
            console.log('🔍 requestAllStores response:', {
                storesCount: data.stores.length,
                totalStores: data.pagination.total,
                pages: data.pagination.pages,
                includePromotors
            });
            return data.stores; // Return just the stores array for compatibility
        }

        // ✅ Fallback for old response format (backward compatibility)
        return Array.isArray(data) ? data : [];
    }

    /**
     * Fetch all stores with caching (optimized endpoint)
     * @param {boolean} forceRefresh - Force refresh the cache
     * @returns {Promise<Object>} - { success, cached, data: { stores, total, computeTime } }
     */
    async requestStoresCached(forceRefresh = false) {
        const response = await axios.get(this.createGetStoresCachedUrl(forceRefresh), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateStoreAuditor(payload) {
        const response = await axios.post(this.createUpdateAuditorUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestStoresByAuditor(auditor) {
        const response = await axios.get(this.createStoresByAuditorUrl(auditor), await this.makeHeaders());
        return await response.data;
    }

    async requestStoresByClient(client) {
        const response = await axios.get(this.createStoresByClientUrl(client), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreById(id) {
        const response = await axios.get(this.createGetStoreByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateStoreById(id, payload) {
        const response = await axios.post(this.createUpdateStoreByIdUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    // -------- STORE CHECKS --------
    async requestMonthlyStoreChecks(storeId, startDate, endDate) {
        const response = await axios.get(this.createStoreCheckByMonthUrl(storeId, startDate, endDate), await this.makeHeaders());
        return await response.data;
    }

    async requestFilteredStoreChecks(date, userId) {
        const response = await axios.get(this.createStoreCheckFilteredUrl(date, userId), await this.makeHeaders());
        return await response.data;
    }

    async requestAllStoreChecks() {
        const response = await axios.get(this.createAllStoreChecksUrl(10000), await this.makeHeaders());
        const data = await response.data;
        // Handle paginated response format
        return data?.data || data || [];
    }

    async requestStoreCheckById(storeId) {
        const response = await axios.get(this.createStoreCheckById(storeId), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreChecksByClient(clientId, startDate, endDate) {
        const response = await axios.get(this.storeCheckByClientUrl(clientId, startDate, endDate), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreChecksByUser(userId, startDate, endDate) {
        const response = await axios.get(this.createStoreCheckByUserUrl(userId, startDate, endDate), await this.makeHeaders());
        return await response.data;
    }

    // -------- DASHBOARD --------
    async requestDashboardAnalytics(startDate, endDate, clientId = null) {
        const url = this.createDashboardAnalyticsUrl(startDate, endDate, clientId);
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestStoreCheckByContractId(tradeContractId) {
        const response = await axios.get(this.createStoreCheckByContractId(tradeContractId), await this.makeHeaders());
        return await response.data;
    }

    // ------- CALENDAR --------
    async requestCreateCalendar(payload) {
        const response = await axios.post(this.createOrUpdateCalendarUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestCalendarByUser(userId) {
        const response = await axios.get(this.createCalendarByUserUrl(userId), await this.makeHeaders());
        return await response.data;
    }

    // ------- PERMISSIONS --------
    async requestPermissions() {
        const response = await axios.get(this.createPermissionsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdatePermissions(role, payload) {
        const response = await axios.put(this.createPermissionsUrl(role), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestResetPermissions(role) {
        const response = await axios.delete(this.createPermissionsUrl(role), await this.makeHeaders());
        return await response.data;
    }

    // ------- PRICE --------
    async requestCreatePrice(payload) {
        const response = await axios.post(this.createPriceUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestStorePriceList(storeId) {
        const response = await axios.get(this.createStorePriceListUrl(storeId), await this.makeHeaders());
        return await response.data;
    }

    async requestSaveStorePriceList(storeId, payload) {
        const response = await axios.post(this.createStorePriceListUrl(storeId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteStorePriceList(storeId) {
        const response = await axios.delete(this.createStorePriceListUrl(storeId), await this.makeHeaders());
        return await response.data;
    }

    async requestStorePriceListsByClient(clientId) {
        const response = await axios.get(this.createStorePriceListsByClientUrl(clientId), await this.makeHeaders());
        return await response.data;
    }

    async requestResolveStorePrice(storeId, date) {
        const response = await axios.get(this.createResolveStorePriceUrl(storeId, date), await this.makeHeaders());
        return await response.data;
    }

    async requestPricesByType(type, date) {
        const response = await axios.get(this.createGetPriceByTypeUrl(type, date), await this.makeHeaders());
        return await response.data;
    }

    async requestPrices() {
        const response = await axios.get(this.createGetPricesUrl(), await this.makeHeaders());
        return await response.data;
    }

    // ------- PRODUCTS --------
    async requestProducts(page = 1, limit = 50, category = null, search = null, includeInactive = false) {
        const response = await axios.get(this.createProductsUrl(page, limit, category, search, includeInactive), await this.makeHeaders());
        return await response.data;
    }

    async requestProductCategories() {
        const response = await axios.get(this.createProductCategoriesUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestCreateProduct(payload) {
        const response = await axios.post(this.createProductsBaseUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateProductById(id, payload) {
        const response = await axios.put(this.createProductByIdUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteProductById(id) {
        const response = await axios.delete(this.createProductByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestProductsBulkUpsert(products) {
        const response = await axios.post(this.createProductsBulkUpsertUrl(), { products }, await this.makeHeaders());
        return await response.data;
    }

    async requestUploadProductImage(file) {
        const formData = new FormData();
        formData.append('image', file);

        const currentToken = getAuthCookie();
        const response = await axios.post(this.createProductsUploadImageUrl(), formData, {
            headers: {
                token: currentToken,
                'x-client': 'admin',
                ...companyHeaders()
            }
        });

        return await response.data;
    }

    // ------- STORE TYPES --------
    async requestCreateStoreType(payload) {
        const response = await axios.post(this.createStoreTypeUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestStoreTypes() {
        const response = await axios.get(this.createStoreTypesUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreTypeByValue(value) {
        const response = await axios.get(this.createStoreTypeByValueUrl(value), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreTypeById(id) {
        const response = await axios.get(this.createStoreTypeByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreTypeOverrides(value) {
        const response = await axios.get(this.createStoreTypeOverridesUrl(value), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreTypeOverride(storeId) {
        const response = await axios.get(this.createStoreTypeOverrideUrl(storeId), await this.makeHeaders());
        return await response.data;
    }

    async requestSaveStoreTypeOverride(storeId, payload) {
        const response = await axios.post(this.createStoreTypeOverrideUrl(storeId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteStoreTypeOverride(storeId) {
        const response = await axios.delete(this.createStoreTypeOverrideUrl(storeId), await this.makeHeaders());
        return await response.data;
    }

    async requestSaveStoreTypeOverrides(payload) {
        const response = await axios.post(this.createStoreTypeOverridesBulkUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestRemoveStoreTypeOverrides(payload) {
        const response = await axios.post(this.createStoreTypeOverridesBulkUrl('remove'), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestInteractionSummary(date) {
        const response = await axios.get(this.createInteractionSummaryUrl(date), await this.makeHeaders());
        return await response.data;
    }

    async requestPromoterRouteEfficiency(userId, date) {
        const response = await axios.get(this.createPromoterRouteEfficiencyUrl(userId, date), await this.makeHeaders());
        return await response.data;
    }

    async requestStoreCheckRecency(startDate, endDate) {
        const response = await axios.get(this.createStoreCheckRecencyUrl(startDate, endDate), await this.makeHeaders());
        return await response.data;
    }

    async requestCompetitorBrands(category) {
        const response = await axios.get(this.createCompetitorBrandsUrl(category), await this.makeHeaders());
        return await response.data;
    }

    async requestCompetitorCategoryConfig() {
        const response = await axios.get(this.createCompetitorCategoryConfigUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateCompetitorCategoryConfig(payload) {
        const response = await axios.put(this.createCompetitorCategoryConfigUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestSegmentationTypes() {
        const response = await axios.get(this.createSegmentationTypesUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestCreateSegmentationType(payload) {
        const response = await axios.post(this.createSegmentationTypesUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateSegmentationType(id, payload) {
        const response = await axios.put(this.createSegmentationTypeUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDistributorPriceVisuals(priceType, startDate, endDate, productId) {
        const response = await axios.get(
            this.createDistributorPriceVisualsUrl(priceType, startDate, endDate, productId),
            await this.makeHeaders()
        );
        return await response.data;
    }

    async requestDistributorPriceAnomalyThreshold() {
        const response = await axios.get(this.createDistributorPriceAnomalyThresholdUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateDistributorPriceAnomalyThreshold(payload) {
        const response = await axios.put(this.createDistributorPriceAnomalyThresholdUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestReviewDistributorPriceObservation(payload) {
        const response = await axios.post(this.createDistributorPriceObservationReviewUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    // ------- LEVANTAMIENTO GENERAL --------

    async requestLevantamientoGeneral(params = {}, signal) {
        const response = await axios.get(this.createLevantamientoGeneralUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestLevantamientoPriceBySku(params = {}, signal) {
        const response = await axios.get(this.createLevantamientoPriceBySkuUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestLevantamientoPriceTrend(params = {}, signal) {
        const response = await axios.get(this.createLevantamientoPriceTrendUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestLevantamientoComparison(params = {}, signal) {
        const response = await axios.get(this.createLevantamientoComparisonUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestLevantamientoRebuild() {
        const response = await axios.post(this.createLevantamientoRebuildUrl(), {}, await this.makeHeaders());
        return await response.data;
    }

    async requestShelfUsage(params = {}, signal) {
        const response = await axios.get(this.createShelfUsageUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestShareOfShelf(params = {}, signal) {
        const response = await axios.get(this.createShareOfShelfUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestShareOfShelfStores(params = {}, signal) {
        const response = await axios.get(this.createShareOfShelfStoresUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestNumericDistribution(params = {}, signal) {
        const response = await axios.get(this.createNumericDistributionUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestNumericDistributionStores(params = {}, signal) {
        const response = await axios.get(this.createNumericDistributionStoresUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestNumericDistributionStoreProducts(params = {}, signal) {
        const response = await axios.get(this.createNumericDistributionStoreProductsUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestProductAvailability(params = {}, signal) {
        const response = await axios.get(this.createProductAvailabilityUrl(params), { ...(await this.makeHeaders()), signal });
        return await response.data;
    }

    async requestAllProducts() {
        const response = await axios.get(this.createAllProductsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestLevantamientoFacets() {
        const response = await axios.get(this.createLevantamientoFacetsUrl(), await this.makeHeaders());
        return await response.data;
    }

    // ------- TRADE --------
    async requestCreateTradeContract(payload) {
        const response = await axios.post(this.createTradeContractUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestTradeContracts() {
        const response = await axios.get(this.createAllTradeContractsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestCreateTradeCampaign(payload) {
        const response = await axios.post(this.createTradeCampaignUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestTradeCampaigns() {
        const response = await axios.get(this.createAllTradeCampaignsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestTradeCampaignById(campaignId) {
        const response = await axios.get(this.createTradeCampaignByIdUrl(campaignId), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateTradeCampaign(campaignId, payload) {
        const response = await axios.post(this.createUpdateTradeCampaignUrl(campaignId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateTradeContract(contractId, payload) {
        const response = await axios.post(this.createUpdateTradeContractUrl(contractId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateTradeContractStorePrice(contractId, storeId, price) {
        const response = await axios.post(this.createTradeContractStorePriceUrl(contractId), { storeId, price }, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateTradeCampaignStorePrice(campaignId, storeId, price) {
        const response = await axios.post(this.createTradeCampaignStorePriceUrl(campaignId), { storeId, price }, await this.makeHeaders());
        return await response.data;
    }

    async requestTradeContractById(contractId) {
        const response = await axios.get(this.createTradeContractByIdUrl(contractId), await this.makeHeaders());
        return await response.data;
    }

    async requestContractsByStore(storeId) {
        const response = await axios.get(this.createContractsByStoreUrl(storeId), await this.makeHeaders());
        return response.data;
    }

    async requestContractsByClient(clientId) {
        const response = await axios.get(this.createContractsByClientUrl(clientId), await this.makeHeaders());
        return response.data;
    }

    async requestCampaignsByStore(storeId) {
        const response = await axios.get(this.createCampaignsByStoreUrl(storeId), await this.makeHeaders());
        return response.data;
    }

    async requestCampaignsByClient(clientId) {
        const response = await axios.get(this.createCampaignsByClientUrl(clientId), await this.makeHeaders());
        return response.data;
    }

    async requestPromotorAnalytics(period = 'week', limit = 10, channel = 'all', tagId = null, startDate = null, endDate = null) {
        const response = await axios.get(this.createPromotorAnalyticsUrl(period, limit, channel, tagId, startDate, endDate), await this.makeHeaders());
        return await response.data;
    }

    async requestPromotorPerformance(promotorId, days = 30) {
        const response = await axios.get(this.createPromotorPerformanceUrl(promotorId, days), await this.makeHeaders());
        return await response.data;
    }

    async requestPolls() {
        const response = await axios.get(this.createPollUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestDeletePoll(id) {
        const response = await axios.delete(this.createPollByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    // -------- TAGS --------
    async requestTags(filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createTagsUrl()}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestTagById(tagId) {
        const response = await axios.get(this.createTagByIdUrl(tagId), await this.makeHeaders());
        return await response.data;
    }

    async requestCreateTag(payload) {
        const response = await axios.post(this.createTagsUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateTag(tagId, payload) {
        const response = await axios.put(this.createTagByIdUrl(tagId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteTag(tagId) {
        const response = await axios.delete(this.createTagByIdUrl(tagId), await this.makeHeaders());
        return await response.data;
    }

    async requestAssignTag(tagId, payload) {
        const response = await axios.post(this.createAssignTagUrl(tagId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestRemoveTag(tagId, payload) {
        const response = await axios.post(this.createRemoveTagUrl(tagId), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestTagItems(tagId, filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createTagItemsUrl(tagId)}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    /** All active tag assignments of a model type: `{ items: [{ modelId, modelType, tagId: tag }] }`. */
    async requestTagAssignments(modelType) {
        const response = await axios.get(`${this.apiUrl}/tags/assignments?modelType=${encodeURIComponent(modelType)}`, await this.makeHeaders());
        return response.status === 200 ? response.data : null;
    }

    async requestItemTags(modelType, modelId) {
        const response = await axios.get(this.createItemTagsUrl(modelType, modelId), await this.makeHeaders());
        return await response.data;
    }

    async requestSOSAnalysis(tagId) {
        const response = await axios.get(this.createSOSAnalysisUrl(tagId), await this.makeHeaders());
        return await response.data;
    }

    // -------- ACTIVITY LOGS --------
    async requestActivityLogs(filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createActivityLogsUrl()}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestActivityLogById(id) {
        const response = await axios.get(this.createActivityLogByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestActivityLogsStats(filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createActivityLogsStatsUrl()}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestActivityLogsDeactivations(filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createActivityLogsDeactivationsUrl()}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestActivityLogsByEntity(entityType, entityId, filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createActivityLogsByEntityUrl(entityType, entityId)}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestActivityLogsByUser(userId, filters = {}) {
        const params = new URLSearchParams(filters).toString();
        const url = `${this.createActivityLogsByUserUrl(userId)}${params ? `?${params}` : ''}`;
        const response = await axios.get(url, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditFieldOptions() {
        const response = await axios.get(this.createAuditFieldOptionsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateAuditFieldOptions(payload) {
        const response = await axios.put(this.createAuditFieldOptionsUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAnalytics() {
        const response = await axios.get(this.createAuditAdminAnalyticsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditProducts(params = {}) {
        const response = await axios.get(this.createAuditAdminAuditProductsUrl(params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditProductFacets() {
        const response = await axios.get(this.createAuditAdminAuditProductFacetsUrl(), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditProductById(id) {
        const response = await axios.get(this.createAuditAdminAuditProductByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestUpdateAuditAdminAuditProduct(id, payload) {
        const response = await axios.put(this.createAuditAdminAuditProductByIdUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditRecords(params = {}) {
        const response = await axios.get(this.createAuditAdminAuditRecordsUrl(params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminStoreChecks(params = {}) {
        const response = await axios.get(this.createAuditAdminStoreChecksUrl(params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditRecordById(id) {
        const response = await axios.get(this.createAuditAdminAuditRecordByIdUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminStoreCheckRecords(id) {
        const response = await axios.get(this.createAuditAdminStoreCheckRecordsUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminProductDuplicates(params = {}) {
        const response = await axios.get(this.createAuditAdminProductDuplicatesUrl(params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminProductMerge(payload) {
        const response = await axios.post(this.createAuditAdminProductMergeUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminProductsBulk(payload) {
        const response = await axios.patch(this.createAuditAdminProductsBulkUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminRecordsBulk(payload) {
        const response = await axios.patch(this.createAuditAdminRecordsBulkUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminReviewQueue(params = {}) {
        const response = await axios.get(this.createAuditAdminReviewQueueUrl(params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditorStats(userId, params = {}) {
        const response = await axios.get(this.createAuditAdminAuditorStatsUrl(userId, params), await this.makeHeaders());
        return await response.data;
    }

    async requestAuditAdminAuditorActivity(userId, params = {}) {
        const response = await axios.get(this.createAuditAdminAuditorActivityUrl(userId, params), await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteAuditAdminAuditRecord(id) {
        const response = await axios.delete(this.createAuditAdminAuditRecordDeleteUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestPatchAuditAdminAuditRecordCurrency(id, payload) {
        const response = await axios.patch(this.createAuditAdminAuditRecordCurrencyUrl(id), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestBulkDeleteAuditAdminAuditRecords(payload) {
        const response = await axios.post(this.createAuditAdminAuditRecordsBulkDeleteUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestBulkCurrencyAuditAdminAuditRecords(payload) {
        const response = await axios.post(this.createAuditAdminAuditRecordsBulkCurrencyUrl(), payload, await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteAuditAdminStoreCheck(id) {
        const response = await axios.delete(this.createAuditAdminStoreCheckDeleteUrl(id), await this.makeHeaders());
        return await response.data;
    }

    async requestDeleteAuditAdminAuditProduct(id, params = {}) {
        const response = await axios.delete(this.createAuditAdminAuditProductDeleteUrl(id, params), await this.makeHeaders());
        return await response.data;
    }
}

export default API;
